import type { StateCreator, StoreMutatorIdentifier } from 'zustand';

export interface HistoryState {
    past: any[];
    future: any[];
    canUndo: boolean;
    canRedo: boolean;
    undo: () => void;
    redo: () => void;
    clearHistory: () => void;
}

type HistoryMiddleware = <
    T,
    Mps extends [StoreMutatorIdentifier, unknown][] = [],
    Mcs extends [StoreMutatorIdentifier, unknown][] = []
>(
    config: StateCreator<T, Mps, Mcs>,
    options?: {
        limit?: number;
        exclude?: (keyof T)[];
        equality?: (a: T, b: T) => boolean;
    }
) => StateCreator<T & HistoryState, Mps, Mcs>;

type HistoryMiddlewareImpl = <T>(
    config: StateCreator<T, [], []>,
    options?: {
        limit?: number;
        exclude?: (keyof T)[];
        equality?: (a: T, b: T) => boolean;
    }
) => StateCreator<T & HistoryState, [], []>;

const historyMiddlewareImpl: HistoryMiddlewareImpl = (config, options = {}) => {
    const { limit = 50, exclude = [], equality } = options;

    return (set, get, api) => {
        let past: any[] = [];
        let future: any[] = [];
        let isApplyingHistory = false;

        // Initialize the base state
        const baseState = config(
            (partial, replace) => {
                // Skip history tracking if we're applying history
                if (isApplyingHistory) {
                    set(partial as any, replace);
                    return;
                }

                // Get current state before update  
                const currentState = get();

                // Apply the update
                set(partial as any, replace);

                // Get new state after update
                const newState = get();

                // Check if state actually changed (excluding history-related fields)
                const shouldTrack = !equality || !equality(currentState as any, newState as any);

                if (shouldTrack) {
                    // Create snapshot excluding specified fields and history state
                    const snapshot = { ...currentState };

                    // Remove excluded fields
                    exclude.forEach(key => {
                        delete (snapshot as any)[key];
                    });

                    // Remove history-related fields
                    delete (snapshot as any).past;
                    delete (snapshot as any).future;
                    delete (snapshot as any).canUndo;
                    delete (snapshot as any).canRedo;
                    delete (snapshot as any).undo;
                    delete (snapshot as any).redo;
                    delete (snapshot as any).clearHistory;

                    // Add to past
                    past = [...past.slice(-(limit - 1)), snapshot];
                    future = []; // Clear future on new action

                    // Update history state
                    set({
                        canUndo: true,
                        canRedo: false
                    } as any);
                }
            },
            get,
            api
        );

        // Add history methods to the state
        return {
            ...baseState,
            past,
            future,
            canUndo: false,
            canRedo: false,
            undo: () => {
                if (past.length === 0) return;

                const currentState = get();
                const previous = past[past.length - 1];

                // Create snapshot of current state for future
                const snapshot = { ...currentState };
                exclude.forEach(key => {
                    delete (snapshot as any)[key];
                });
                delete (snapshot as any).past;
                delete (snapshot as any).future;
                delete (snapshot as any).canUndo;
                delete (snapshot as any).canRedo;
                delete (snapshot as any).undo;
                delete (snapshot as any).redo;
                delete (snapshot as any).clearHistory;

                past = past.slice(0, -1);
                future = [snapshot, ...future];

                isApplyingHistory = true;
                set({
                    ...previous,
                    canUndo: past.length > 0,
                    canRedo: true
                } as any);
                isApplyingHistory = false;
            },
            redo: () => {
                if (future.length === 0) return;

                const currentState = get();
                const next = future[0];

                // Create snapshot of current state for past
                const snapshot = { ...currentState };
                exclude.forEach(key => {
                    delete (snapshot as any)[key];
                });
                delete (snapshot as any).past;
                delete (snapshot as any).future;
                delete (snapshot as any).canUndo;
                delete (snapshot as any).canRedo;
                delete (snapshot as any).undo;
                delete (snapshot as any).redo;
                delete (snapshot as any).clearHistory;

                past = [...past, snapshot];
                future = future.slice(1);

                isApplyingHistory = true;
                set({
                    ...next,
                    canUndo: true,
                    canRedo: future.length > 0
                } as any);
                isApplyingHistory = false;
            },
            clearHistory: () => {
                past = [];
                future = [];
                set({
                    canUndo: false,
                    canRedo: false
                } as any);
            }
        };
    };
};

export const historyMiddleware = historyMiddlewareImpl as unknown as HistoryMiddleware;
